// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// ** Components
import ReactApexcharts from './ReactApexcharts';
import { axisTitleSx, axisValueSx } from './styles';

// ** Styles
import { DIN } from '@/styles/globalStyles';

// ** Types
import type { ApexOptions } from 'apexcharts';
import type { GraphTableData, GraphTableSeriesStyle } from './types';

// ─── helpers ────────────────────────────────────────────────────────────────

function styleToColor(style: GraphTableSeriesStyle) {
	switch (style) {
		case 'solidOrange':
			return '#DF7A00';
		case 'solidGrey':
			return '#717171';
		case 'dashedOrange':
			return '#BD4F19';
		case 'dashedGrey':
			return '#353535';
		default:
			return '#DF7A00';
	}
}

function styleToDash(style: GraphTableSeriesStyle) {
	return style === 'dashedOrange' || style === 'dashedGrey' ? 8 : 0;
}

function formatTwoDigitYear(raw: unknown) {
	const str = String(raw ?? '');
	const years = str.match(/\d{4}/g);
	if (years?.length) {
		const year = Number.parseInt(years[years.length - 1] ?? '', 10);
		if (Number.isFinite(year)) return String(year % 100).padStart(2, '0');
	}
	const last2 = str.match(/(\d{2})(?!.*\d)/)?.[1];
	return last2 ? String(last2).padStart(2, '0') : str;
}

// ─── LegendDot ──────────────────────────────────────────────────────────────

function LegendDot({ color, muted = false }: { color: string; muted?: boolean }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			aria-hidden="true"
			focusable="false"
		>
			<circle cx="8" cy="8" r="8" fill={color} opacity={muted ? 0.3 : 1} />
		</svg>
	);
}

// ─── GraphView ──────────────────────────────────────────────────────────────

type Props = {
	data: GraphTableData;
	lineStyleMode: 'mixed' | 'solid' | 'dashed';
};

export default function GraphView({ data, lineStyleMode }: Props) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const [hidden, setHidden] = React.useState<Record<string, boolean>>({});
	const chartContainerRef = React.useRef<HTMLDivElement>(null);

	const stripBrowserNativeChartTooltips = React.useCallback(() => {
		const root = chartContainerRef.current;
		if (!root) return;

		// ApexCharts injects SVG <title> nodes / title attributes which trigger
		// the browser’s native grey tooltip (often just "Chart").
		root.querySelectorAll('title').forEach((n) => {
			n.remove();
		});
		root.querySelectorAll<HTMLElement>('[title]').forEach((el) => {
			el.removeAttribute('title');
		});
	}, []);

	const colors = React.useMemo(() => data.series.map((s) => styleToColor(s.style)), [data.series]);

	const dashArray = React.useMemo(() => {
		if (lineStyleMode === 'solid') return data.series.map(() => 0);
		if (lineStyleMode === 'dashed') return data.series.map(() => 8);
		return data.series.map((s) => styleToDash(s.style));
	}, [data.series, lineStyleMode]);

	const visibleSeriesCount = React.useMemo(
		() => data.series.reduce((acc, s) => acc + (hidden[s.name] ? 0 : 1), 0),
		[data.series, hidden]
	);

	const noGraphSelected = visibleSeriesCount === 0;
	const axisPlaceholderColor = theme.palette.text.disabled;

	const yAxisLabelText = noGraphSelected ? 'Y-Achse' : data.yAxisTitle;
	const yAxisIsPlaceholder = noGraphSelected;
	const xAxisLabelText = noGraphSelected ? 'X-Achse' : data.xAxisTitle;
	const xAxisIsPlaceholder = noGraphSelected;

	const applyXAxisLabelStyle = React.useCallback((activeIdx: number | null) => {
		const labels = chartContainerRef.current?.querySelectorAll<SVGTextElement>('.apexcharts-xaxis-label');
		if (!labels?.length) return;
		labels.forEach((el, i) => {
			const active = activeIdx !== null && i === activeIdx;
			el.style.fontWeight = active ? '700' : '';
			el.style.textDecoration = active ? 'underline' : '';
			if (active) {
				el.setAttribute('font-weight', '700');
			} else {
				el.removeAttribute('font-weight');
			}
			el.querySelectorAll<SVGTSpanElement>('tspan').forEach((ts) => {
				ts.style.fontWeight = active ? '700' : '';
				ts.style.textDecoration = active ? 'underline' : '';
			});
		});
	}, []);

	const options = React.useMemo<ApexOptions>(
		() => ({
			chart: {
				type: 'line',
				toolbar: { show: false },
				zoom: { enabled: false },
				animations: {
					enabled: true,
					easing: 'easeinout',
					speed: 900,
					animateGradually: { enabled: true, delay: 120 },
					dynamicAnimation: { enabled: true, speed: 450 }
				},
				events: {
					mouseMove: (_event, _ctx, config) => {
						const idx = config?.dataPointIndex;
						applyXAxisLabelStyle(typeof idx === 'number' && idx >= 0 ? idx : null);
					},
					mouseLeave: () => applyXAxisLabelStyle(null),
					mounted: () => requestAnimationFrame(stripBrowserNativeChartTooltips),
					updated: () => requestAnimationFrame(stripBrowserNativeChartTooltips)
				}
			},
			colors,
			stroke: { width: 3, curve: 'straight', dashArray },
			grid: {
				borderColor: '#D9D9D9',
				padding: {
					top: 0,
					left: isMobile ? 48 : 64,
					right: 0
				}
			},
			markers: { size: 0, hover: { size: 5 } },
			dataLabels: { enabled: false },
			legend: { show: false },
			xaxis: {
				categories: data.categories,
				crosshairs: { show: false },
				tooltip: { enabled: false },
				title: {
					text: undefined,
					style: {
						color: axisTitleSx.color,
						fontFamily: axisTitleSx.fontFamily,
						fontSize: axisTitleSx.fontSize,
						fontWeight: axisTitleSx.fontWeight as number
					}
				},
				labels: {
					style: {
						colors: axisValueSx.color,
						fontFamily: axisValueSx.fontFamily,
						fontSize: axisValueSx.fontSize,
						fontWeight: axisValueSx.fontWeight as number
					},
					formatter: (val: string) => (isMobile ? formatTwoDigitYear(val) : val)
				}
			},
			yaxis: {
				floating: true,
				min: 0,
				title: {
					text: undefined,
					style: {
						color: axisTitleSx.color,
						fontFamily: axisTitleSx.fontFamily,
						fontSize: axisTitleSx.fontSize,
						fontWeight: axisTitleSx.fontWeight as number
					}
				},
				labels: {
					offsetX: 0,
					style: {
						colors: axisValueSx.color,
						fontFamily: axisValueSx.fontFamily,
						fontSize: axisValueSx.fontSize,
						fontWeight: axisValueSx.fontWeight as number,
						cssClass: 'nrw-gtw-yaxis-label'
					}
				}
			},
			tooltip: {
				shared: true,
				intersect: false,
				custom: ({ series, dataPointIndex, w }) => {
					const xVal = String(
						data.categories?.[dataPointIndex] ??
							w?.globals?.categoryLabels?.[dataPointIndex] ??
							w?.globals?.labels?.[dataPointIndex] ??
							''
					);
					const rows = (series as Array<Array<number | null>>)
						.map((s, idx) => {
							const yVal = s[dataPointIndex];
							if (yVal == null) return '';
							const name: string = w?.globals?.seriesNames?.[idx] ?? '';
							const color: string = w?.globals?.colors?.[idx] ?? '#000';
							return `
								<div style="display:flex;align-items:center;gap:6px;font-size:12px;font-weight:400;line-height:18px;">
									<span style="display:inline-block;width:14px;height:3px;background:${color};border-radius:2px;flex-shrink:0;"></span>
									${name}:&nbsp;<span style="font-weight:700;">${yVal}</span>
								</div>
							`;
						})
						.join('');
					const rowCount = (series as Array<Array<number | null>>).filter((s) => s[dataPointIndex] != null).length;
					const height = 32 + rowCount * 24;
					return `
						<div style="
							min-width:135px;min-height:72px;height:${height}px;
							border-radius:8px;background:#FFF;
							box-shadow:0 0 16px 0 rgba(0,0,0,0.16);
							padding:12px 14px;box-sizing:border-box;
							display:flex;flex-direction:column;justify-content:center;gap:4px;
							font-family:${DIN};color:#000;
						">
							<div style="font-size:14px;font-weight:400;line-height:24px;letter-spacing:-0.05px;color:#353535;margin-bottom:4px;">${xVal}</div>
							${rows}
						</div>
					`;
				}
			}
		}),
		[applyXAxisLabelStyle, colors, dashArray, data.categories, isMobile, stripBrowserNativeChartTooltips]
	);

	const series = React.useMemo(() => {
		const hiddenRow = data.categories.map(() => null) as Array<number | null>;
		return data.series.map((s) => ({
			name: s.name,
			data: hidden[s.name] ? hiddenRow : s.data
		}));
	}, [data.categories, data.series, hidden]);

	return (
		<>
			{/* Legend */}
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					alignItems: 'center',
					gap: 3,
					mb: { xs: 1, sm: 2 },
					mt: { xs: 3, md: 6, lg: 10 }
				}}
			>
				{data.series.map((s, idx) => (
					<ButtonBase
						key={s.name}
						onClick={() => setHidden((prev) => ({ ...prev, [s.name]: !prev[s.name] }))}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							borderRadius: 1,
							opacity: hidden[s.name] ? 0.45 : 1,
							transition: 'opacity 0.18s ease, transform 0.15s ease',
							'&:hover': { transform: 'scale(1.06)' },
							'&:active': { transform: 'scale(0.96)' }
						}}
					>
						<LegendDot color={colors[idx] ?? '#DF7A00'} muted={Boolean(hidden[s.name])} />
						<Typography
							variant="caption"
							component="span"
							sx={{
								fontWeight: 500,
								letterSpacing: '-0.2px',
								color: 'common.black',
								textDecoration: hidden[s.name] ? 'line-through' : 'none',
								textUnderlineOffset: theme.spacing(0.375)
							}}
						>
							{s.name}
						</Typography>
					</ButtonBase>
				))}
			</Box>

			{/* Y-axis title */}
			{yAxisLabelText ? (
				<Box sx={{ mb: 0, mt: 6 }}>
					<Typography
						sx={{
							...axisTitleSx,
							textAlign: 'left',
							...(yAxisIsPlaceholder ? { color: axisPlaceholderColor } : null)
						}}
					>
						{yAxisLabelText}
					</Typography>
				</Box>
			) : (
				<Box sx={{ mb: 0 }} />
			)}

			{/* Chart */}
			<Box
				ref={chartContainerRef}
				sx={{
					width: '100%',
					aspectRatio: '1131 / 508',
					minHeight: 280,
					'& .nrw-gtw-yaxis-label': { textAnchor: 'start' }
				}}
			>
				{noGraphSelected ? (
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							textAlign: 'center',
							px: 2,
							borderRadius: 1,
							border: '1px dashed rgba(0,0,0,0.18)'
						}}
					>
						<Typography variant="body-md" sx={{ color: 'text.secondary' }}>
							Bitte wählen Sie mindestens eine Datenreihe aus.
						</Typography>
					</Box>
				) : (
					<ReactApexcharts type="line" options={options} series={series} width="100%" height="100%" />
				)}
			</Box>

			{/* X-axis title */}
			{xAxisLabelText ? (
				<Box sx={{ mt: { xs: 1, sm: 2 } }}>
					<Typography
						sx={{
							...axisTitleSx,
							fontSize: '12px',
							lineHeight: '12px',
							textAlign: 'center',
							...(xAxisIsPlaceholder ? { color: axisPlaceholderColor } : null)
						}}
					>
						{xAxisLabelText}
					</Typography>
				</Box>
			) : null}
		</>
	);
}
